import axios from "axios";
import React from "react";
import { ConfirmDialog } from "primereact/confirmdialog";

import {
  APIKey,
  FormSectionElements,
  FormSections,
  FormSubSections,
  ProgramContainer
} from "../../entities/formTemplateDataType";
import FormValidationUtils from "../../utilities/FormValidationUtils";
import Utils from "../../utilities/Utils";
import { getParentSection, getParentSubSection, getRootComponent, getSectionBySequence } from "../../utilities/ElementUtils";
import DataUtils from "../../utilities/DataUtils";
import { SpinnerComponent } from "../../commonComponent/SpinnerComponent";
import ReCaptchaV3 from "../captcha/CaptchComponent";
import ReCaptchaButton from "../captcha/captcha-button";
import { parseInlineStyle } from "../../utilities/HtmlUtils";
import { checkPagination, checkSelectedService, checkUserProf, setBaseHeaders, setBasePayload } from "../../utilities/submitUtils";
import {
  cleanDataDueError,
  getAppResponseData,
  getRoutingPageOnAppResponse,
  handleResponseData
} from "../../response/AppResponseDataHanler";
import { copySimpleNonEmptyAttributes } from "../../utilities/URLUtils";
import { removeEmptyPayloadFields } from "../../utilities/JsonUtils";
import SectionUtils from "../../utilities/SectionUtils";
import { DynamicUrlInterpreter } from '../../utilities/DynamicUrlInterpreter';
import { getMockButtonApiResponse, isE2EBypassEnabled } from "../../utilities/E2EBypassUtils";
// import { gaEvent, gaPageview } from "../../utilities/GAUtils";
import { RichTextComponent } from "./RichTextComponent";
import Card from "../card";
import '../card/style.css';
import CustomPopup from "./PopupComponent";
import SessionStorageService from "../../services/SessionStorageService";
import { getEnvVar, SCRIPT_ENV } from "../../config/RuntimeConfig";
import './ButtonComponent.css';
import { findSectionElementByFieldName } from "../../utilities/ElementUtils";
import { MultiPartEntity, multiPartValidateResponse, uploadMultiPartFilesParallel } from "../../utilities/fileUtils";
import { ReportData, updateValidationState } from "../../utilities/ValidationStateUtils";

interface Props {
  field: FormSectionElements;
  editable: boolean;
  disabled?: boolean;
  forceRenderCancelInline?: boolean;
  title;
  labelControlArray;
  requiredField;
  isEng: boolean;
  callback,
  returnIndex?: number,
  callbackStepIndex?: (index: number, returnIndex?: number) => void;
  callbackValidate?: (errors: any, fromAPI?: boolean) => void
}

interface State {
  isBusy: boolean
  pageError: boolean;
  showPopup: boolean;
}

export class ButtonComponent extends React.Component<Props, State> {
  HtmlToReactParser = require('html-to-react').Parser;
  private recaptchaToken?: string;
  constructor(props: Props) {
    super(props);

    this.state = {
      isBusy: false,
      pageError: false,
      showPopup: false,
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
    // only act on new validation errors; avoid triggering another state change here
    if (!prevState.pageError && this.state.pageError) {
      const firstHeader = document.querySelector('#error-container') as HTMLDivElement;
      if (firstHeader) {
        firstHeader.focus();
      }
    }
  }

  arrayToString = (_a) => {
    if (Array.isArray(_a)) {
      return _a.join(' ');
    } else {
      return [_a]
    }
  }
  handleSubmitNoRecaptha = () => {
    this.handleButtonClick(null);
  }

  private resetFieldValidationState = (field: FormSectionElements) => {
    field.ErrorMessage = null;
    field.ErrorMessageFRA = null;
    field.Valid = true;
  }

  private validateBeforeSubmit = (field: FormSectionElements): boolean => {
    if (field.ValidateStep === 'Y') {
      const section = getParentSection(field);
      const invalidControlList = FormValidationUtils.invalidControls(section, field.SubSectionToDuplicate);

      if (invalidControlList?.length > 0) {
        this.props.callbackValidate?.(invalidControlList);
        const recordNotFoundField = field.findFieldByName ? field.findFieldByName('Record__not__found') : null;
        if (recordNotFoundField) {
          recordNotFoundField.Visible = false;
        }
        cleanDataDueError(field, invalidControlList);
        this.setState({ pageError: true });
        return false;
      }
    }

    this.setState({ pageError: false });
    return true;
  }

  private resetFormDirtyState = (programContainer: ProgramContainer) => {
    if (!programContainer) {
      return;
    }
    SectionUtils.resetDirtyFlags(programContainer);
    try {
      //(programContainer as any).IsDirty = false;
    } catch {
      // Ignore setter issues; flags already cleared.
    }
  }

  handleSubmitError = (response?: any) => {

    this.prepareSurveyUrlParams(response);

    if (this.props.field.SubmitButton.apiErrorRedirectPage) {
      const sequence = Math.max(0, this.props.field.SubmitButton.apiErrorRedirectPage - 1);
      cleanDataDueError(this.props.field)
      this.props.callbackStepIndex(sequence);
    } else {
      const page = getSectionBySequence(this.props.field, 10);
      if (page) {
        this.props.callbackStepIndex(Math.max(0, page.Sequence - 1));
      }
    }
  }

  private prepareSurveyUrlParams(response: any) {
    let programContainer = getRootComponent(this.props?.field) as ProgramContainer;
    const surveyUrlParamsField = this.props?.field?.findFieldByName('surveyUrlParams')
    if (surveyUrlParamsField?.Value) {
      surveyUrlParamsField.Value = {
        ...surveyUrlParamsField?.Value || {},
        ...(Utils.isNotNullOrEmpty(response?.data?.errorCode) && { code: response.data?.errorCode }),
        sts: '400'
      }
      this.props.callback(programContainer);
    }
  }

  handleBusinessError = (response) => {

    response?.data?.isHardstop && this.prepareSurveyUrlParams(response);

    const field = this.props.field;
    if (response && response.data && response.data.errorCode) {
      const HtmlToReactParser = require('html-to-react').Parser;
      const htmlToReactParser = new HtmlToReactParser();
      field.ErrorMessage = htmlToReactParser.parse(response.data.messageEn);
      field.ErrorMessageFRA = htmlToReactParser.parse(response.data.messageFr);
      field.Valid = false;
      this.props.callbackValidate([field], true);
      return;
    }
  }

  private getBusinessFailureInfo = (data: any): { failed: boolean; messageEn?: string; messageFr?: string } => {
    if (!data || typeof data !== "object") {
      return { failed: false };
    }

    const failureStatuses = new Set(["failed", "failure", "error", "errored", "unsuccessful"]);
    const statusKeys = new Set(["status", "processingstatus", "resultstatus", "submissionstatus"]);

    const normalize = (value: unknown) =>
      typeof value === "string" ? value.trim().toLowerCase() : "";

    const traverse = (node: any): boolean => {
      if (node == null) {
        return false;
      }

      if (Array.isArray(node)) {
        return node.some((item) => traverse(item));
      }

      if (typeof node !== "object") {
        return false;
      }

      if (node.success === false || node.isSuccess === false || node.ok === false || node.hasErrors === true) {
        return true;
      }

      for (const [rawKey, value] of Object.entries(node)) {
        const key = rawKey.toLowerCase();

        if (statusKeys.has(key) && failureStatuses.has(normalize(value))) {
          return true;
        }

        if (key === "errors" && Array.isArray(value) && value.length > 0) {
          return true;
        }
      }

      return Object.values(node).some((value) => traverse(value));
    };

    if (!traverse(data)) {
      return { failed: false };
    }

    return {
      failed: true,
      messageEn:
        data?.messageEn ||
        data?.message ||
        data?.errorMessage ||
        "Submission failed. Please review your incident report and try again.",
      messageFr:
        data?.messageFr ||
        data?.message ||
        data?.errorMessage ||
        "La soumission a échoué. Veuillez vérifier le rapport d'incident et réessayer.",
    };
  }

  private normalizeErrorText = (errors: unknown): string => {
    if (typeof errors === "string") {
      return errors.trim();
    }

    if (!Array.isArray(errors)) {
      return "";
    }

    return errors
      .map((entry: any) => {
        if (typeof entry === "string") {
          return entry.trim();
        }
        if (entry && typeof entry === "object") {
          const field = String(entry.fieldName ?? "Validation").trim();
          const message = String(entry.error ?? entry.message ?? "").trim();
          return message ? `${field}: ${message}` : field;
        }
        return "";
      })
      .filter((part: string) => part.length > 0)
      .join("; ");
  }

  private extractIncidentReportRows = (data: any): ReportData[] => {
    const raw = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
    if (!Array.isArray(raw)) {
      return [];
    }

    return raw
      .filter((item: any) => item && typeof item === "object")
      .map((item: any) => ({
        nameOfReport: String(item?.nameOfReport ?? ""),
        callNumber: String(item?.callNumber ?? ""),
        errors: this.normalizeErrorText(item?.errors),
        status: String(item?.status ?? "")
      }))
      .filter((row) => row.nameOfReport || row.callNumber || row.errors || row.status);
  }

  private persistIncidentReportRows = (rows: ReportData[]) => {
    if (!rows || rows.length === 0) {
      return;
    }

    sessionStorage.setItem("lastApiResponse", JSON.stringify(rows));
    sessionStorage.setItem("finalReviewData", JSON.stringify(rows));
    sessionStorage.setItem(
      "validationHasErrors",
      rows.some((row) => typeof row.errors === "string" && row.errors.trim().length > 0) ? "true" : "false"
    );
    updateValidationState(rows);

    window.dispatchEvent(new Event("refreshReportTable"));
  }


  handleButtonClick = async (captchaToken?: string) => {
    const field = this.props.field;
    this.resetFieldValidationState(field);

    if (field?.IsCancelButton) {
      await this.handleCancelAction();
      return;
    }

    const section = getParentSection(field);

    if (!this.validateBeforeSubmit(field)) {
      return;
    }

    if (field?.EventScript) {
      const functions = {
          checkInvalidControls: ((section: FormSections, subSectionToDuplicate: string) => FormValidationUtils.invalidControls(section, field.SubSectionToDuplicate))
      }
      
      const customJSFunc = eval(`(${field.EventScript})`);
      const returnedValue = customJSFunc(field, SCRIPT_ENV, functions);
      /**
       * Event Script is to handle any logic that needs to be performed on click event.
       * If you wanted to perform any side effect on click event output you can add it below inside the IF
       */
      if (returnedValue) {
       // Add optional logic that needs to be executed and Do Not return here
      }
    }
    const skipApiField = field.findFieldByName ? field.findFieldByName('skipApi') : null;
    if (skipApiField?.Value && field?.StepperScript) {
        const customJSFunc = eval(`(${field.StepperScript})`);
        customJSFunc((index) => {
            this.props.callbackStepIndex(index)
        });
        return;
    }
    let programContainer = getRootComponent(field) as ProgramContainer;

    const apiUrl = field.Api || field.SubmitButton?.api;
    const submissionMethod = field.SubmissionMethod || field.SubmitButton?.SubmissionMethod;

    if (apiUrl) {
      if (field.SaveDraftToDatabase) {
        const processSuccessful = await DataUtils.saveDraftToDatabase(programContainer, apiUrl);
        if (processSuccessful && field.SaveDraftToDatabaseSuccessMessageElement) {
          const messageElement = findSectionElementByFieldName(field.parent.ListOfFormSectionElements, field.SaveDraftToDatabaseSuccessMessageElement);
          if (messageElement) {
            messageElement.Visible = true;
            messageElement.InvisibleByDefault = false;
            this.props.callback(programContainer);
          }
        } else if (field.apiErrorRedirectPage || field.SubmitButton.apiErrorRedirectPage) {
          this.props.callbackStepIndex((field.apiErrorRedirectPage || field.SubmitButton.apiErrorRedirectPage) - 1);
        }
      } else {
        let api = apiUrl;
        const basePath = programContainer.APIBasePath;
        const isAbsoluteUrl = /^https?:\/\//i.test(api);
        if (basePath && !isAbsoluteUrl) {
          const normalizedBasePath = basePath.endsWith('/') ? basePath : `${basePath}/`;
          api = `${normalizedBasePath}${api.startsWith('/') ? api.substring(1) : api}`;
        }
        api = DynamicUrlInterpreter.interpretUrl(api, {
          programContainer,
          appName: programContainer.CategoryId
        });

        const apiKeyKey = field.ApiKeyKey || field.SubmitButton?.apiKeyKey;
        const apiKeyValue = field.ApiKeyValue || field.SubmitButton?.apiKeyValue;
        const apiKey: APIKey =
          apiKeyKey && apiKeyValue
            ? { key: apiKeyKey, value: apiKeyValue }
            : programContainer.ApiKeyKey && programContainer.ApiKeyValue
            ? {
                key: programContainer.ApiKeyKey,
                value: programContainer.ApiKeyValue,
              }
            : null;

        const sendMetadataOnly = field.SubmitButton?.sendMetadataOnly;
        let entity = DataUtils.collectFormValue(programContainer, "N", "en", { sendMetadataOnly });
        entity = setBasePayload(
          entity,
          this.props.isEng ? "EN" : "FR",
          captchaToken
        );
        entity = checkUserProf(entity, programContainer.Profile);
        entity = checkSelectedService(entity);
        entity = checkPagination(entity);

        delete entity.pagination;
        
        // Use customApiPayload if set by EventScript (replaces entire entity with custom payload)
        const customPayload = (field as any).customApiPayload;
        if (customPayload && typeof customPayload === 'object') {
          entity = customPayload;
        }

        // Strip empty fields from payload for regulated-personnel individual/update only
        const isRegulatedPersonnel = programContainer?.CategoryId?.toLowerCase().startsWith('regulated-personnel');
        if (isRegulatedPersonnel && api.includes('individual/update')) {
          entity = removeEmptyPayloadFields(entity);
        }

        const token = SessionStorageService.getAuthToken(programContainer.AuthRequired) ?? programContainer.Profile?.token;
        const headers = setBaseHeaders(
          "application/json",
          token,
          apiKey
        );
        // Only expect JSON response when opted in (avoids breaking buttons that trigger downloads or other response types)
        const expectJsonResponse = field.SubmitButton?.responseType === 'json' || (field as any).expectJsonResponse === true;
        const requestConfig: any = { headers };
        if (expectJsonResponse) {
          requestConfig.responseType = 'json';
        }
        let response;

        if (programContainer.AuthRequired == 'ps' || programContainer.AuthRequired == 'bps') {
          entity.publicSecureUserId = programContainer.Profile?.userId;
        }
        try {
          this.setState({ isBusy: true });
          programContainer.Response=null;
          if (isE2EBypassEnabled()) {
            const preferredStatusCode = field.SubmitButton?.apiSuccessRedirectPage?.[0]?.statusCode;
            response = getMockButtonApiResponse(preferredStatusCode);
            programContainer.Response = response;
          } else {
            switch (submissionMethod) {
              case "post":
                if (entity.multiPart) {
                  // response = await uploadMultiPartFiles(api, entity as MultiPartEntity, requestConfig)
                  response = await uploadMultiPartFilesParallel(api, entity as MultiPartEntity, requestConfig)
                } else {
                  response = await axios.post(api, entity, requestConfig);
                }
                programContainer.Response=response
                break;
              case "get":
                const queryParams = copySimpleNonEmptyAttributes(entity, {});
                const configs = {
                  ...{ headers },
                  ...queryParams ? { params: queryParams } : undefined,
                };
                response = await axios.get(api, configs);
                programContainer.Response=response
                break;
              case "put":
                if (entity.multiPart) {
                  response = { status: 200, data: multiPartValidateResponse(entity as MultiPartEntity) };
                } else {
                  response = await axios.put(api, entity, requestConfig);
                  programContainer.Response=response
                }
                break;
            }
          }

          if (response.status >= 400) {
            this.handleSubmitError(response);
            return;
          }

          const businessFailure = this.getBusinessFailureInfo(response?.data);
          if (businessFailure.failed) {
            const batchRows = this.extractIncidentReportRows(response?.data);
            if (batchRows.length > 0) {
              this.persistIncidentReportRows(batchRows);
            }
            this.handleBusinessError({
              data: {
                errorCode: response?.data?.errorCode || "BUSINESS_FAILURE",
                messageEn: businessFailure.messageEn,
                messageFr: businessFailure.messageFr,
              },
            });
            return;
          }

          const isHardstop = response?.data?.isHardstop;

          if (typeof isHardstop === 'boolean') {
            if (isHardstop) {
              this.prepareSurveyUrlParams(response);
              const errorSection = programContainer?.ListOfFormSections?.find(formSection => formSection?.IsTechErrorPage);
              if (errorSection) {
                errorSection.apiResponse = response.data;
                this.props.callback(programContainer);
                this.resetFormDirtyState(programContainer);
                errorSection?.Sequence && this.props.callbackStepIndex(errorSection?.Sequence - 1);
                return;
              }
            } else {
              this.handleBusinessError(response);
            }
          }

        } catch (ex: any) {
          console.log(ex);
          // If the API returned a 409 (Conflict / duplicate), show the message from the API
          if (ex?.response?.status === 409 && ex?.response?.data) {
            const errorMsg = typeof ex.response.data === 'string'
                ? ex.response.data
                : ex.response.data.messageEn || ex.response.data.message || JSON.stringify(ex.response.data);
            const field = this.props.field;
            field.ErrorMessage = errorMsg;
            field.ErrorMessageFRA = typeof ex.response.data === 'string'
                ? ex.response.data
                : ex.response.data.messageFr || errorMsg;
            field.Valid = false;
            this.props.callbackValidate([field], true);
            return;
          }
          // this.props.callbackStepIndex(9);
          this.handleSubmitError();
          return;
        } finally {
          this.setState({ isBusy: false });
          if ((field as any).customApiPayload) {
            delete (field as any).customApiPayload;
          }
        }

        if (!response) {
          // this.props.callbackStepIndex(9);
          this.handleSubmitError();
          return;
        }

        if (!response?.data && Array.isArray((response as any)?.results)) {
          const firstSuccess = (response as any).results.find(result => result?.success && result?.data !== undefined);
          if (firstSuccess) {
            (response as any).data = firstSuccess.data;
          }
        }

        let currentEntity = getAppResponseData(field, response);
        if (!currentEntity) {
          const fieldFound = field.findFieldByName('Record__not__found');
          if (fieldFound) {
            fieldFound.Visible = true;
          }
          // const threeTime = field.findFieldByName('3__time__limit__reached');
          // if (threeTime) {
          //   threeTime.Visible = true;
          // }
          this.props.callback(programContainer);
          return;
        }

        const pageSeq = getRoutingPageOnAppResponse(field, response);
        if (pageSeq > 0) {
          if (field?.Script) {
            const customJSFunc = eval(`(${field.Script})`);
            await customJSFunc(field, SCRIPT_ENV, response);
          }
          this.props.callback(programContainer);
          this.resetFormDirtyState(programContainer);
          this.props.callbackStepIndex(pageSeq - 1);
          return;
        }

        handleResponseData(field, entity, currentEntity, this.props.callbackValidate);
        //this.props.callback(currentEntity.data);
        this.props.callback(programContainer);
        this.resetFormDirtyState(programContainer);

        if (programContainer?.CategoryId?.toUpperCase()?.startsWith('ODR')) {
          if (currentEntity?.statusCode && currentEntity?.statusCode !== 0) {
            const index = field.SubmitButton?.apiSuccessRedirectPage?.find(page =>
              String(page?.statusCode) === String(currentEntity?.statusCode));
            if (index) {
              this.props.callbackStepIndex(index.redirectPage);
            } else {
              console.debug('Error Occurred: Unable to process statusCode in response: ' + currentEntity?.statusCode);
            }
          } else {
            console.debug('Error Occurred: Unable to process statusCode in response: ' + currentEntity?.statusCode);
          }
        }

        if (programContainer?.CategoryId?.toUpperCase()?.startsWith('OTRIS')) {
          if (currentEntity.statusCode == 2) {
            this.props.callbackStepIndex(7);
            //  this.props.callback(programContainer);
            return;
          }

          if (currentEntity.statusCode == 3) {
            this.props.callbackStepIndex(8);
            //  this.props.callback(programContainer);
            return;
          }

          if (currentEntity.statusCode == 5 || currentEntity.statusCode == 55) {
            this.props.callbackStepIndex(9);
            return;
          }

          if (currentEntity.statusCode == 13) {
            this.props.callbackStepIndex(10);
            return;
          }
          // programContainer.Entity= currentEntity.data;

          // DataUtils.assignFormValue(programContainer);

          // this.props.callback(programContainer);
        }
      }
    }

    FormValidationUtils.runJavascript(field);

    if (field?.Script) {
      const customJSFunc = eval(`(${field.Script})`);
      await customJSFunc(field, SCRIPT_ENV);
    }

    if (field.StepperScript) {
      const customJSFunc = eval(`(${field.StepperScript})`);
      customJSFunc((index) => {
        let destination = index;
        if (this.props.returnIndex != null) {
          destination = this.props.returnIndex;
          this.props.callbackStepIndex(destination);
        } else {
          if (typeof destination === "string") {
            const root = getRootComponent(field);
            const foundSection = root.ListOfFormSections.find(section => section.ENUTitle == destination);
            const indexNum = root.ListOfFormSections.filter(p => p.Visible).indexOf(foundSection);
            this.props.callbackStepIndex(indexNum)
          } else {
            this.props.callbackStepIndex(destination);
          }
        }
      });
    }


    //this.props.callback(getRootComponent(field));

    const currentSection = getParentSection(this.props.field);
    const root = currentSection.parent;
    const gaTrackingId = root.GATrackingID;

    // if (gaTrackingId) {
    //   gaEvent({ action: 'click', category: 'Continue', label: this.props.isEng ? currentSection.ENUTitle : currentSection.FRATitle }, gaTrackingId);
    // }
        // }
    if(field.sectionGroupToDuplicate && field.ReadOnly !== false) {
      const root = getRootComponent(field);

      if(!field.isDeleteButton) {
        const current = getParentSection(field);
        SectionUtils.duplicateSection(root, field.sectionGroupToDuplicate, current);
        this.props.callbackStepIndex(current.Sequence);
      }else{
        const current = getParentSection(field);
        for (let i = root.ListOfFormSections.length - 1; i >= 0; i--) {
          const section = root.ListOfFormSections[i];
          if (section.duplicateGroupID === current.duplicateGroupID &&section.duplicateGroupIndex === current.duplicateGroupIndex) {
            root.ListOfFormSections.splice(i, 1);
          }
        }
        root.ListOfFormSections.forEach((section, index) => {
          section.Sequence = index + 1;
          section.Id = String(index + 1);
        });


        const groupedArray: FormSections[][] = Object.values(
          root.ListOfFormSections.filter(i => i.duplicateGroupID === current.duplicateGroupID)
              .reduce((acc, item) => {
                (acc[item.duplicateGroupIndex] ??= []).push(item);
                return acc;
                }, {} as Record<number, FormSections[]>)
              );

        const sectionsForDup: FormSections[] = root.duplicatableGroups![current.duplicateGroupID];

        groupedArray.forEach((sections, ind) => {
          sections.forEach((src, i) => {
            if (sectionsForDup[i]){
              const groupIndex = (ind + 1).toString();
              src.stepTitle = sectionsForDup[i].stepTitle?.replace('#seq#', groupIndex);
              src.stepTitleFRA = sectionsForDup[i].stepTitleFRA?.replace('#seq#', groupIndex);
              src.ENUTitle = sectionsForDup[i].ENUTitle?.replace('#seq#', groupIndex);
              src.FRATitle = sectionsForDup[i].FRATitle?.replace('#seq#', groupIndex);
              SectionUtils.replaceStep(src,src)
              src.ENUDescription = sectionsForDup[i].ENUDescription?.replace('#seq#', groupIndex);
              src.FRADescription = sectionsForDup[i].FRADescription?.replace('#seq#', groupIndex);
            }
          });
        })
        this.props.callbackStepIndex(groupedArray[0][0].Sequence-1);
        
      }
    }

    console.info('After button click, root component: ', root);

    if (field.SubSectionToDuplicate && field.ReadOnly !== false) {
      let section = getParentSection(field);
      let subsectionToDuplicate: FormSubSections;
      let currentNumberOfDuplicates = 0;
      let indexToInsertDuplicate = 0;
      // let subsec = SectionUtils.getSubSectionByTitle(field.SubSectionToDuplicate, section);
      for (let i = 0; i < section.ListOfFormSubSections.length; i++) {
        const sub = section.ListOfFormSubSections[i];
        if (sub.ENUTitle == field.SubSectionToDuplicate && sub.CanBeDuplicate && !sub.IsDuplicate) {
          subsectionToDuplicate = sub;
          indexToInsertDuplicate = i + 1;
        }
        else if (sub.ENUTitle == field.SubSectionToDuplicate && sub.IsDuplicate) {
          currentNumberOfDuplicates++;
          indexToInsertDuplicate = i + 1;
        }
      }

      // PLP: limit number of plates to 10
      if (subsectionToDuplicate && currentNumberOfDuplicates < 9) {
        let subsecShallowCopy = { ...subsectionToDuplicate } as FormSubSections;
        subsecShallowCopy.IsDuplicate = true;
        subsecShallowCopy.ReadOnlyElements = false;
        subsecShallowCopy.ListOfFormSectionElements = [];

        subsecShallowCopy.ValidateStep = 'Y';
        subsecShallowCopy.SubmitButton = {
          ...(subsecShallowCopy.SubmitButton ?? {}), ValidateStep: 'Y'
        };


        subsectionToDuplicate.ListOfFormSectionElements.forEach(orginalEle => {
          let ele = { ...orginalEle } as FormSectionElements;
          ele.parent = subsecShallowCopy;
          if (ele.Datatype != 'Label' && ele.Datatype != "HTML" && ele.Datatype != "TechError") {
            ele.Value = null;
            ele.day = null;
            ele.month = null;
            ele.year = null;
            ele.Visible = true;
            ele.Touched = false;
          }

          if (ele.Datatype == "SelectWithRichText" || ele.Datatype == "RadioButtonWithRichText") {
            ele.SelectionCode = "";
          }
          if (ele.Datatype == "CheckboxWithTextbox") {
            ele.Checked = false;
          }
          if (ele.Datatype == "Address") {
            ele.Address = null;
          }
          if (ele.Datatype === 'File') {
            ele.Value = null;
          }
          subsecShallowCopy.ListOfFormSectionElements.push(ele);
        });

        section.ListOfFormSubSections.forEach(s => {
          if (s.ENUTitle == field.SubSectionToDuplicate) {
            // When KeepDuplicatesEditable is enabled, keep all subsections editable
            // Otherwise, set all existing subsections to read-only (original behavior)
            if (s.KeepDuplicatesEditable !== true) {
              s.ReadOnlyElements = true;
              s.ValidateStep = 'N';
              if (s.SubmitButton) {
                s.SubmitButton.ValidateStep = 'N';
              }
            }
          }
        })
        section.ListOfFormSubSections.splice(indexToInsertDuplicate, 0, subsecShallowCopy);
        const newsubsectiondiv = document.querySelector('#subSec_new') as HTMLDivElement;
        if (newsubsectiondiv) {
          newsubsectiondiv.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }

    }

    this.props.callback(root);

  }

  cardButtonContent = () => {
    if (this.props.field.Datatype === 'CardButton') {
      const icon = this.props.isEng ? this.props.field.callOutBoxBody : this.props.field.callOutBoxBodyFRA;
      return (
        <div className="card-button-content">
          {icon && (
            <div className="card-button-image-container">
              <RichTextComponent
                field={this.props.field}
                editable={false}
                isEng={this.props.isEng}
                value={icon}
                title={''}
                labelControlArray={[]}
                requiredField={false}
                callbackStepIndex={(currenPageIndex, returnIndex) => this.props.callbackStepIndex(currenPageIndex, returnIndex)}
                callbackElement={() => this.props.callback(getRootComponent(this.props.field))}
              />
            </div>
          )}
          <div className="card-button-text-container">
            {/* {field.ENUTitle && <h3 className="card-button-title">{field.ENUTitle}</h3>}
                    {field.ENUDescription && <p className="card-button-description">{field.ENUDescription}</p>} */}
            <RichTextComponent
              field={this.props.field}
              editable={false}
              isEng={this.props.isEng}
              value={this.props.isEng ? this.props.field.callOutBoxTitle : this.props.field.callOutBoxTitleFRA}
              title={''}
              labelControlArray={[]}
              requiredField={false}
              callbackStepIndex={(currenPageIndex, returnIndex) => this.props.callbackStepIndex(currenPageIndex, returnIndex)}
              callbackElement={() => this.props.callback(getRootComponent(this.props.field))}
            />
          </div>
        </div>
      );
    }
    return this.props.isEng ? this.props.field.ENUTitle : this.props.field.FRATitle;
  }

  cardContent = () => (
    <Card content={this.cardButtonContent()} className="notBordered ontario-margin-bottom-0-!" />
  );

  getButtonText = () => {
    const customText = this.props.isEng ? this.props.field.CustomButtonText : this.props.field.CustomButtonTextFRA;
    const defaultText = this.props.isEng ? this.props.field.ENUTitle : this.props.field.FRATitle;
    return customText || defaultText;
  }

  buttonContent = () => {
    return this.props.field.Datatype === 'CardButton' ? this.cardContent() : this.getButtonText();
  }

  handleButtonClickWithPopup = () => {
    if (this.props.field.ShowPopup || this.props.field.IsCancelButton) {
      const field = this.props.field;
      this.resetFieldValidationState(field);
      if (field.IsCancelButton || this.validateBeforeSubmit(field)) {
        this.setState({ showPopup: true });
      }
      return;
    }
    this.handleButtonClick();
  }

  handlePopupSave = (recaptchaToken?: string) => {
    this.setState({ showPopup: false });
    if (this.props.field?.IsCancelButton) {
      this.handleCancelAction();
      return;
    }
    this.handleButtonClick(recaptchaToken);
    this.recaptchaToken = recaptchaToken;
  }

  handlePopupCancel = () => {
    this.setState({ showPopup: false });
    this.recaptchaToken = undefined;
  }

  handleRecaptchaClick = (token?: string) => {
    if (this.props.field.ShowPopup || this.props.field.IsCancelButton) {
      const field = this.props.field;
      this.resetFieldValidationState(field);
      if (field.IsCancelButton || this.validateBeforeSubmit(field)) {
        this.recaptchaToken = token;
        this.setState({ showPopup: true });
      }
      return;
    }
    this.handleButtonClick(token);
  }

  render() {
    let field = this.props.field;


    if (!field.Visible || (!this.props.editable&&field.hideInSummary)) return null;

    if (
      (field?.IsCancelButton || field?.IsSaveDraftButton) &&
      !this.props.forceRenderCancelInline &&
      getParentSection(field)?.KeepContinueButtonLeft
    ) {
      return null;
    }

    if(field.sectionGroupToDuplicate && field.ReadOnly !== false) {
      const root = getRootComponent(field);
      const match = field.sectionGroupToDuplicate.match(/-(\d+)$/);
      const initNumberOfDupSection: number | null = match ? Number(match[1]) : null;
      const lastSectionForDupGroup = root.ListOfFormSections.flat().filter(item => item.duplicateGroupID === field.sectionGroupToDuplicate).at(-1);
      const section=getParentSection(field)
      const isLastSection = section.duplicateGroupIndex === lastSectionForDupGroup?.duplicateGroupIndex;
      const totalDupSize = new Set(root.ListOfFormSections.filter(section => section.duplicateGroupID === field.sectionGroupToDuplicate).map(section => section.duplicateGroupIndex)).size;
      if(!field.isDeleteButton) {
        if(!isLastSection || totalDupSize>9) return null; //max 10 duplications
      }else{
        if(totalDupSize<=initNumberOfDupSection) return null;
      }
    }

    let controlArray = [];
    let title = this.getButtonText();

    if (field.InitScript && Utils.isNotNullOrEmpty(field.InitScript)) {
      const functions = {
          checkInvalidControls: ((section: FormSections, subSectionToDuplicate: string) => FormValidationUtils.invalidControls(section, field.SubSectionToDuplicate))
      }
      const customJSFunc = eval(`(${field.InitScript})`);
      try {
        customJSFunc(field, SCRIPT_ENV, functions);
      } catch (ex) {
        console.log(ex);
      }
    }

    let ClassName = "ontario-button " + this.arrayToString(this.props.field.ClassName);
    ClassName = field.Datatype === 'CardButton' ? " card-button" : ClassName;
    const inlineStyle = parseInlineStyle(field.InlineStyle);
    const wrapperStyle: React.CSSProperties = {
      textAlign: 'left',
      ...(inlineStyle ?? {}),
    };

    const hasRecaptcha = !!(field.Recaptcha || field.SubmitButton?.enableRecaptcha);
    const isReadOnly = !!this.props.disabled || field.Disabled;

    const buttonJSX = (<div
      style={wrapperStyle}
      key={"button_" + getParentSubSection(field).Sequence + "_" + field.Id}
    >
      {!hasRecaptcha ? (
        <button
          className={ClassName}
          id={`button_${getParentSubSection(field).Sequence}_${field.Id}`}
          title={title}
          aria-disabled={isReadOnly}
          onClick={(e) => {
            e.preventDefault();
            if (isReadOnly) {
              return;
            }
            this.handleButtonClickWithPopup();
            e.stopPropagation();  // To avoid propagating to 'click outside' listener.
          }}
        >
          {this.buttonContent()}
        </button>
      ) : (
        <ReCaptchaButton
          id={`recaptchbutton_${getParentSubSection(field).Sequence}_${field.Id}`}
          title={title}
          readOnly={isReadOnly}
          action={field.Recaptcha?.action || 'submitAction'}
          buttonHandler={this.handleRecaptchaClick}
        >
          {this.buttonContent()}
        </ReCaptchaButton>
      )}

      <div>
        <SpinnerComponent isBusy={this.state.isBusy} />
      </div>
    </div>);

    if (this.props.field.Centered || this.props.field.ButtonProps?.buttonPosition === 'center') {
      controlArray.push(
        <div style={{ display: 'flex', justifyContent: 'center', width: '100%', }} key={"button_centered_" + getParentSubSection(field).Sequence + "_" + field.Id}> {buttonJSX}</div>
      );
    } else if (this.props.field.ButtonProps?.buttonPosition === 'right') {
      controlArray.push(
        <div style={{ display: 'inline-block' }} key={"button_right_" + getParentSubSection(field).Sequence + "_" + field.Id}> {buttonJSX}</div>
      );
    } else {
      controlArray.push(
        buttonJSX
      );
    }

    const baseButtonText = this.getButtonText();
    const popupTitle = this.props.isEng
      ? field.PopupTitle || (field.IsCancelButton ? "Are you sure you want to leave?" : "")
      : field.PopupTitleFRA || (field.IsCancelButton ? "Voulez-vous quitter?" : "");
    const popupMessage = this.props.isEng
      ? field.PopupMessage || (field.IsCancelButton ? "Your changes on this page will not be saved." : "")
      : field.PopupMessageFRA || (field.IsCancelButton ? "Vos changements sur cette page ne seront pas enregistres." : "");
    const popupSaveText = this.props.isEng
      ? field.PopupSaveButtonText || (field.IsCancelButton ? "Yes, leave" : baseButtonText || "Save")
      : field.PopupSaveButtonTextFRA || (field.IsCancelButton ? "Oui, quitter" : baseButtonText || "Enregistrer");
    const popupCancelText = this.props.isEng
      ? field.PopupCancelButtonText || (field.IsCancelButton ? "Stay" : "Cancel")
      : field.PopupCancelButtonTextFRA || (field.IsCancelButton ? "Rester" : "Annuler");

    const popupPrimaryButton = hasRecaptcha ? (
      <ReCaptchaButton
        key={"recaptchbutton-popup"}
        title={popupSaveText}
        action={field.Recaptcha?.action || 'submitAction'}
        readOnly={isReadOnly}
        buttonHandler={this.handlePopupSave}
      >
        {popupSaveText}
      </ReCaptchaButton>
    ) : (
      <button
        className="ontario-button ontario-button--primary recaptcha-button"
        onClick={(e) => {
          e.preventDefault();
          this.handlePopupSave();
        }}
      >
        {popupSaveText}
      </button>
    );

    const htmlToReactParser = new this.HtmlToReactParser();
    const content = (
      <>
        {controlArray}
        {this.state.showPopup && (!field.PopupDialogConfigs?.ModalDialog) && (
          <CustomPopup
            key="custom-popup"
            isVisible={this.state.showPopup}
            title={popupTitle}
            closable={true}
            handleModalClose={this.handlePopupCancel}
            customClass="custom-popup"
          >
            <div className="popup-content">
              <p>{htmlToReactParser.parse(popupMessage)}</p>
              <div className="popup-buttons">
                {popupPrimaryButton}
                {!field.HidePopupCancelButton && <button
                  className="ontario-button ontario-button--secondary"
                  onClick={(e) => {
                    e.preventDefault();
                    this.handlePopupCancel();
                  }}
                >
                  {popupCancelText}
                </button>}
              </div>
            </div>
          </CustomPopup>
        )}
        {this.state.showPopup && (field.PopupDialogConfigs?.ModalDialog) && (<ConfirmDialog
          className="dialog-style"
          visible={this.state.showPopup}
          header={this.props.isEng ? field.PopupTitle || field.PopupDialogConfigs?.PopupTitle : field.PopupTitleFRA || field.PopupDialogConfigs?.PopupTitleFRA}
          message={this.props.isEng ? field.PopupMessage || field.PopupDialogConfigs?.PopupMessage : field.PopupMessageFRA || field.PopupDialogConfigs?.PopupMessageFRA}
          rejectLabel={this.props.isEng ? field.PopupCancelButtonText || field.PopupDialogConfigs?.PopupLeftButtonText : field.PopupCancelButtonTextFRA || field.PopupDialogConfigs?.PopupLeftButtonTextFRA}
          rejectClassName={field.PopupDialogConfigs?.PopupLeftButtonClassName || "ontario-button ontario-button--primary"}
          acceptLabel={this.props.isEng ? field.PopupSaveButtonText || field.PopupDialogConfigs?.PopupRightButtonText : field.PopupSaveButtonTextFRA || field.PopupDialogConfigs?.PopupRightButtonTextFRA}
          acceptClassName={field.PopupDialogConfigs?.PopupRightButtonClassName || "ontario-button ontario-button--tertiary"}
          icon={field.PopupDialogConfigs?.PopupMessageIcon || null}
          closable={field.PopupDialogConfigs?.PopupCloseable || false}
          accept={() => this.handleCancelAction()}
          reject={() => this.handlePopupCancel()}
          onHide={() => this.setState({ showPopup: false })}
        />)}        
      </>
    );

    return hasRecaptcha ? (
      <ReCaptchaV3 lang={this.props.isEng ? 'en' : 'fr'}>
        {content}
      </ReCaptchaV3>
    ) : content;
  }

  private getCancelRedirectUrl = () => {
    const field = this.props.field;
    const fallback = this.props.isEng ? getEnvVar('SERVICEONTARIO_URL') : getEnvVar('SERVICEONTARIO_URL_F');
    return (this.props.isEng ? field.CancelRedirectUrl : field.CancelRedirectUrlFRA) || fallback;
  }

  private handleCancelAction = async () => {
    const field = this.props.field;
    // Allow custom scripts to run before redirecting.
    if (field?.EventScript) {
      const customJSFunc = eval(`(${field.EventScript})`);
      const returnedValue = customJSFunc(field, SCRIPT_ENV);
      if (returnedValue === false) {
        return;
      }
    }

    if (field?.Script) {
      const customJSFunc = eval(`(${field.Script})`);
      const result = await customJSFunc(field, SCRIPT_ENV);
      if (result === false) {
        return;
      }
    }

    const targetUrl = this.getCancelRedirectUrl();
    if (targetUrl) {
      window.location.href = targetUrl;
    }
  }
}


